/**
 * Feature Flag Bypass Token Helper
 *
 * Manages an in-memory token that allows testers to bypass feature flags
 * by including it as a query parameter (?bypass_token=<token>).
 *
 * The token is held in memory and mirrored to sessionStorage, so it survives the
 * location.reload() that AuthService.login performs, for this tab only. It is never
 * written to localStorage. TokenService.clearStorage (which runs for every logged-out
 * visit) keeps it via preserveFeatureFlagBypassStorage. It is cleared by:
 * - Window/tab close (sessionStorage is per tab)
 * - Logout (AuthService.logout calls resetFeatureFlagBypassToken)
 * - The expired-session redirect (authGuard calls resetFeatureFlagBypassToken)
 * - A different user signing in than the one it was bound to (bindFeatureFlagBypassToken)
 *
 * It is NOT kept out of browser history: the browser records the first visit with
 * ?bypass_token= before main.ts strips it with history.replaceState.
 */

const STORAGE_KEY = 'ff-bypass-token';
const OWNER_KEY = 'ff-bypass-owner';
let bypassToken = '';

/**
 * Resolves a base URL the way every service does: the staging/production environments hold
 * the literal 'https://api.{{brandDomain}}', replaced per call with window.location.host.
 */
export function resolveBrandDomain(url: string): string {
  return (url ?? '').replace('{{brandDomain}}', window.location.host);
}

/**
 * Captures the bypass token from the URL query string.
 *
 * - If the query string contains ?bypass_token=<value>, extracts and stores it
 * - Ignores empty values (keeps previously captured token)
 * - Removes the bypass_token parameter from location.search via history.replaceState
 *   so it does not appear in browser history or later SPA navigation
 * - Preserves other query parameters (affid, sub, etc.)
 *
 * @param searchString - The query string (e.g., '?bypass_token=abc&other=value')
 */
export function captureFeatureFlagBypassToken(searchString: string): void {
  if (!searchString) {
    return;
  }

  const params = new URLSearchParams(searchString);
  const token = params.get('bypass_token');

  // Only update if a non-empty token was found
  if (token) {
    bypassToken = token;
    try {
      sessionStorage.setItem(STORAGE_KEY, token);
      // a freshly captured token is unbound until the next sign-in claims it
      sessionStorage.removeItem(OWNER_KEY);
    } catch {
      // storage unavailable: the in-memory token still works until reload
    }

    // Strip the bypass_token parameter from the URL to keep it out of browser history
    // and avoid it being sent to third-party scripts in the page
    params.delete('bypass_token');

    const newSearch = params.toString() ? `?${params.toString()}` : '';
    const newUrl = `${window.location.pathname}${newSearch}${window.location.hash}`;

    // Use replaceState to update the URL without adding a new history entry,
    // keeping the existing history.state
    window.history.replaceState(window.history.state, '', newUrl);
  }
}

/**
 * Returns the currently captured bypass token.
 *
 * @returns The bypass token, or empty string if none captured
 */
export function getFeatureFlagBypassToken(): string {
  if (!bypassToken) {
    try {
      bypassToken = sessionStorage.getItem(STORAGE_KEY) ?? '';
    } catch {
      bypassToken = '';
    }
  }
  return bypassToken;
}

/**
 * Drops only the in-memory copy, as a page reload does. The sessionStorage mirror stays.
 */
export function forgetInMemoryFeatureFlagBypassToken(): void {
  bypassToken = '';
}

/**
 * Clears the stored bypass token.
 *
 * Called by AuthService.logout.
 */
export function resetFeatureFlagBypassToken(): void {
  bypassToken = '';
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(OWNER_KEY);
  } catch {
    // ignore
  }
}

/**
 * Binds the token to the user who signs in. The first sign-in after capture claims it;
 * a different user signing in later drops it, so a shared tab never inherits it.
 */
export function bindFeatureFlagBypassToken(userId: string | number | undefined): void {
  if (!getFeatureFlagBypassToken() || userId === undefined || userId === null) {
    return;
  }
  const owner = String(userId);
  try {
    const current = sessionStorage.getItem(OWNER_KEY);
    if (!current) {
      sessionStorage.setItem(OWNER_KEY, owner);
    } else if (current !== owner) {
      resetFeatureFlagBypassToken();
    }
  } catch {
    // storage unavailable: nothing is persisted to inherit
  }
}

/**
 * Runs a storage wipe (TokenService.clearStorage) while keeping the bypass token, so it is
 * still there after the location.reload() that follows login.
 */
export function preserveFeatureFlagBypassStorage(wipe: () => void): void {
  let token: string | null = null;
  let owner: string | null = null;
  try {
    token = sessionStorage.getItem(STORAGE_KEY);
    owner = sessionStorage.getItem(OWNER_KEY);
  } catch {
    // ignore
  }
  wipe();
  try {
    if (token) {
      sessionStorage.setItem(STORAGE_KEY, token);
    }
    if (owner) {
      sessionStorage.setItem(OWNER_KEY, owner);
    }
  } catch {
    // ignore
  }
}
